import { GridColumn as Column } from '@progress/kendo-react-grid';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  DeleteButton,
  SecondaryButton
} from 'src/components/shared/Button/Button';
import { BooleanCell } from 'src/components/shared/DataGrid/BooleanCell';
import { BooleanFilterCell } from 'src/components/shared/DataGrid/BooleanFilterCell';
import { DataGrid } from 'src/components/shared/DataGrid/DataGrid';
import { GridCountCell } from 'src/components/shared/DataGrid/GridCountCell';
import {
  HeaderSection,
  HeaderSectionLeft,
  HeaderSectionRight
} from 'src/components/shared/HeaderSection/HeaderSection';
import { Modal } from 'src/components/shared/Modal/Modal';
import { BodyType } from 'src/components/shared/Typography/BodyType';
import { Header1 } from 'src/components/shared/Typography/Header1';
import { useIsFormDirty } from 'src/hooks/useIsFormDirty';
import { useReduxDispatch } from 'src/store';
import { fetchMethods } from 'src/store/methods/methods.actions';
import { fetchParameterGroups } from 'src/store/parametergroups/parametergroups.actions';
import {
  ParameterDetailDto,
  ParameterDto
} from 'src/store/parameters/parameter';
import { mapParameterDetailToEdit } from 'src/store/parameters/parameter.map';
import {
  createParameter,
  deleteParameter,
  editParameter,
  fetchParameters
} from 'src/store/parameters/parameters.actions';
import { ParameterApi } from 'src/store/parameters/parameters.api';
import { selectParameters } from 'src/store/parameters/parameters.selectors';
import { isLoadingParameters } from 'src/store/requests/requests.selectors';
import { ParameterForm } from './ParameterForm';

export const ParametersPage = () => {
  const [currentParameter, setCurrentParameter] = useState<ParameterDto | null>(
    null
  );
  const [parameter, setParameter] =
    useState<Nullable<ParameterDetailDto>>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [isFormDirty, callbackRef] = useIsFormDirty();

  const { t } = useTranslation();

  const dispatch = useReduxDispatch();

  const parameters = useSelector(selectParameters);
  const loadingParameters = useSelector(isLoadingParameters);

  useEffect(() => {
    dispatch(fetchMethods());
    dispatch(fetchParameterGroups());
    dispatch(fetchParameters());
  }, [dispatch]);

  useEffect(() => {
    if (currentParameter) {
      setShowDeleteModal(true);
    }
  }, [currentParameter]);

  const onCancel = () => {
    setCurrentParameter(null);
    setParameter(null);
    setShowDeleteModal(false);
    setShowEditModal(false);
  };

  const onCreate = () => {
    setShowEditModal(true);
  };

  const onEdit = async (parameter: ParameterDto) => {
    const response = await ParameterApi.getById(parameter.id);
    if (response.ok) {
      setParameter(response.data);
      setShowEditModal(true);
    }
  };

  const onDelete = (parameter: ParameterDto) => {
    setCurrentParameter(parameter);
  };

  const onConfirmDelete = () => {
    if (currentParameter) {
      dispatch(deleteParameter(currentParameter.id, currentParameter));
      setCurrentParameter(null);
      setShowDeleteModal(false);
    }
  };

  const onSubmit = async (parameter: ParameterDetailDto) => {
    const dto = mapParameterDetailToEdit(parameter);
    if (dto.id) {
      await dispatch(editParameter(dto.id, dto));
    } else {
      await dispatch(createParameter(dto));
    }
    dispatch(fetchParameters());
    onCancel();
  };

  return (
    <>
      <HeaderSection className="mb-32">
        <HeaderSectionLeft>
          <Header1>{t('parameter.plural')}</Header1>
          <BodyType>{`${t('crudtext')} ${t('parameter.plural')}`}</BodyType>
        </HeaderSectionLeft>
        <HeaderSectionRight>
          <SecondaryButton onClick={onCreate} icon="la-plus">
            {`${t('create.verb')} ${t('parameter.singular')}`}
          </SecondaryButton>
        </HeaderSectionRight>
      </HeaderSection>

      <DataGrid
        items={parameters}
        loading={loadingParameters}
        onRowClick={({ dataItem }) => onEdit(dataItem)}
        initialSort={[{ field: 'name', dir: 'asc' }]}
      >
        <Column
          field="name"
          width={300}
          title={t('props.name')}
          footerCell={() => (
            <GridCountCell
              count={parameters?.length}
              label={t('parameter.plural')}
            />
          )}
        />
        <Column field="decimals" width={180} title={t('parameter.decimals')} />
        <Column field="description" title={t('props.description')} />
        <Column
          field="parameterGroupName"
          title={t('parameterGroup.singular')}
        />
        <Column
          width="160px"
          field="active"
          title={t('props.active')}
          cell={props => <BooleanCell {...props} />}
          filterCell={props => <BooleanFilterCell {...props} />}
        />
        <Column
          width="160px"
          field="corrected"
          title={t('parameter.corrected')}
          cell={props => <BooleanCell {...props} />}
          filterCell={props => (
            <BooleanFilterCell
              trueLabel={t('props.true')}
              falseLabel={t('props.false')}
              {...props}
            />
          )}
        />
        <Column
          width="160px"
          field="mixParameter"
          title={t('parameter.mixParameter')}
          cell={props => <BooleanCell {...props} />}
          filterCell={props => (
            <BooleanFilterCell
              trueLabel={t('props.true')}
              falseLabel={t('props.false')}
              {...props}
            />
          )}
        />
        <Column
          width="100px"
          filterable={false}
          cell={props => (
            <td>
              <DeleteButton onClick={() => onDelete(props.dataItem)} />
            </td>
          )}
        />
      </DataGrid>

      <Modal
        width={1000}
        header={`${parameter ? t('edit') : t('create.verb')} ${t(
          'parameter.singular'
        )}`}
        confirmText={t('save.verb')}
        onClose={() => onCancel()}
        open={showEditModal}
        allowCommonExitStrategies={!isFormDirty}
      >
        <ParameterForm
          parameter={parameter}
          onCancel={onCancel}
          onSubmit={onSubmit}
          ref={callbackRef}
        />
      </Modal>

      <Modal
        width={500}
        header={`${t('delete.verb')} ${t('parameter.singular')}`}
        confirmText={t('delete.verb')}
        onClose={() => onCancel()}
        onConfirm={() => onConfirmDelete()}
        open={showDeleteModal}
        showActions
      >
        <BodyType>
          {t('parameter.delete')} <q>{currentParameter?.name}</q>?
        </BodyType>
      </Modal>
    </>
  );
};
